import { Injectable, OnDestroy } from '@angular/core';
import { isEqual } from 'lodash';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

export type SpotMeta = {
  spot: string;
  srcLeg: number;
  cross: string;
  manual: boolean;
};

export type ForwardPoints = {
  bid: string;
  bidManual: boolean;
  ask: string;
  askManual: boolean;
};

export type PremiumValueTypeMeta = {
  forwardOrSpot: 'Forward' | 'Spot';
  srcLeg: number;
};

export type ForwardPointsMeta = {
  expirationDate: string;
  currencyPair: string;
  forwardPoints?: ForwardPoints;
  srcLeg: number;
};

type PremiumSettlementDates = BehaviorSubject<BehaviorSubject<string>[]>;

type PremiumValueTypes = BehaviorSubject<BehaviorSubject<string>[]>;
@Injectable({
  providedIn: 'root'
})
export class LegSyncService implements OnDestroy {
  public readonly premiumValueTypes$: PremiumValueTypes = new BehaviorSubject(
    []
  );
  public readonly premiumSettlementDates$: PremiumSettlementDates =
    new BehaviorSubject<BehaviorSubject<string>[]>([]);
  private readonly forwardPoint$List: Record<
    string,
    ReplaySubject<ForwardPointsMeta>
  > = {};
  private readonly spot$List: Record<string, ReplaySubject<SpotMeta>> = {};

  constructor() {}

  public ngOnDestroy(): void {
    Object.values(this.spot$List).forEach(v => v.complete());
    Object.values(this.forwardPoint$List).forEach(v => v.complete());
  }

  public setupLegPreiumValueSync(fields: string[]): void {
    this.premiumValueTypes$.next(fields.map(val => new BehaviorSubject(val)));
  }

  public setupPremiumSettlementDateSync(fields: string[]): void {
    this.premiumSettlementDates$.next(
      fields.map(val => new BehaviorSubject(val))
    );
  }

  public syncPremiumSettlementDate(index: number, date: string): void {
    const dates = this.premiumSettlementDates$.value;
    dates[index].next(date);
  }

  public syncPremiumType(index: number, type: string): void {
    const types = this.premiumValueTypes$.value;
    types[index].next(type);
  }

  public subscribeToSpotChanges(
    cross: string,
    legIndex: number
  ): Observable<SpotMeta> {
    const sub$ = this.getOrCreateSpotSub(cross);
    return sub$.pipe(
      filter(v => v.srcLeg !== legIndex),
      distinctUntilChanged((a, b) => isEqual(a, b))
    );
  }

  public updateSpot(spotMeta: SpotMeta): void {
    const sub$ = this.getOrCreateSpotSub(spotMeta.cross);
    sub$.next(spotMeta);
  }

  public subscribeToForwardPointsChanges(
    meta: ForwardPointsMeta
  ): Observable<ForwardPoints> {
    const sub$ = this.getOrCreateFPSub(meta);
    return sub$.pipe(
      filter(v => v.srcLeg !== meta.srcLeg),
      map(v => v.forwardPoints),
      distinctUntilChanged((a, b) => isEqual(a, b))
    );
  }

  public updateForwardPoints(fpMeta: Required<ForwardPointsMeta>) {
    const sub$ = this.getOrCreateFPSub(fpMeta);
    sub$.next(fpMeta);
  }

  /**
   * Create a sep. subject for each unique currency pair and expiration date
   */

  private getOrCreateSpotSub(cross: string): ReplaySubject<SpotMeta> {
    if (!this.spot$List[cross]) {
      this.spot$List[cross] = new ReplaySubject<SpotMeta>(1);
    }
    return this.spot$List[cross];
  }

  private getOrCreateFPSub(
    meta: ForwardPointsMeta
  ): ReplaySubject<ForwardPointsMeta> {
    const key = `${meta.currencyPair}|${meta.expirationDate}`;
    if (!this.forwardPoint$List[key]) {
      this.forwardPoint$List[key] = new ReplaySubject<ForwardPointsMeta>(1);
    }
    return this.forwardPoint$List[key];
  }
}
